import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  CalendarDays, BookOpen, Brain, FlaskConical, FileText,
  ChevronLeft, ChevronRight, Check, AlertTriangle, RefreshCw,
  Trash2, BarChart3, Clock, Target, Layers, Trophy, LayoutGrid, Spade
} from 'lucide-react';
import { cleanPlannerTitle } from '../utils/cleanTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Type icons & colors ─────────────────────────────────────────────────────
const TYPE_CONFIG = {
  presentacion: { icon: FileText, label: 'Presentación', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  cuestionario: { icon: Brain, label: 'Cuestionario GPC', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  masterclass: { icon: BookOpen, label: 'Masterclass', color: 'bg-white text-black border-gray-300' },
  escape_room: { icon: FlaskConical, label: 'Escape Room', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  card_game: { icon: Spade, label: 'Juego de Cartas', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  simulacro: { icon: Trophy, label: 'Simulacro', color: 'bg-red-50 text-red-700 border-red-200' },
};

// ── Link generator for each activity type ───────────────────────────────────
function getActivityLink(act) {
  if (act.unavailable) return null;
  switch (act.type) {
    case 'presentacion': return `/presentaciones/view/${act.content_id}`;
    case 'cuestionario': return `/quiz/${act.content_id}`;
    case 'masterclass': return act.content_source === 'interactive_lesson'
      ? `/masterclasses/interactivas/${act.content_id}` : `/masterclasses/${act.content_id}`;
    case 'escape_room': return `/escape-room/play/${act.content_id}`;
    case 'card_game': return `/juego-cartas?deck=${act.content_id}&solo=1`;
    case 'simulacro': return `/simulacros/${act.content_id}`;
    default: return null;
  }
}

// ── Setup View ──────────────────────────────────────────────────────────────
const SetupView = ({ user, onPlanGenerated }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [intensity, setIntensity] = useState('moderado');
  const [restDay, setRestDay] = useState(true);
  const [contentSummary, setContentSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    apiClient.get('/planner/content-summary')
      .then(r => setContentSummary(r.data))
      .catch(() => toast.error('Error cargando contenido'));
  }, []);

  // Calculate preview stats when dates change
  useEffect(() => {
    if (!startDate || !endDate) { setPreview(null); return; }
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    if (end <= start) { setPreview(null); return; }
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const totalWeeks = Math.ceil(totalDays / 7);
    const effectiveDays = totalDays - (restDay ? totalWeeks : 0);
    setPreview({ totalDays, totalWeeks, effectiveDays });
  }, [startDate, endDate, restDay, contentSummary]);

  const handleGenerate = async () => {
    if (!startDate || !endDate) { toast.error('Selecciona ambas fechas'); return; }
    setGenerating(true);
    try {
      const res = await apiClient.post('/planner/generate', {
        start_date: startDate, end_date: endDate, intensity, rest_day: restDay,
      });
      const plan = res.data.plan;
      // Save plan
      await apiClient.post('/planner/save', { user_id: user.id, plan });
      toast.success('Plan generado exitosamente');
      onPlanGenerated(plan);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando plan');
    } finally {
      setGenerating(false);
    }
  };

  const intensityOptions = [
    { value: 'leve', label: 'Leve', desc: '1 vuelta completa', icon: '1x' },
    { value: 'moderado', label: 'Moderado', desc: '1.5-2 vueltas', icon: '2x' },
    { value: 'intenso', label: 'Muy intenso', desc: 'Máximas vueltas', icon: 'MAX' },
  ];

  return (
    <div className="space-y-6 pb-24">
      {/* Content Summary */}
      {contentSummary && (
        <div data-testid="content-summary">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Contenido disponible</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { key: 'presentaciones', label: 'Presentaciones', count: contentSummary.totals.presentaciones, Icon: FileText },
              { key: 'cuestionarios', label: 'Cuestionarios', count: contentSummary.totals.cuestionarios, Icon: Brain },
              { key: 'masterclasses', label: 'Masterclasses', count: contentSummary.totals.masterclasses || 0, Icon: BookOpen },
              { key: 'escape_rooms', label: 'Escape Rooms', count: contentSummary.totals.escape_rooms, Icon: FlaskConical },
              { key: 'simulacros', label: 'Simulacros', count: contentSummary.totals.simulacros, Icon: Trophy },
            ].map(({ key, label, count, Icon }) => (
              <div key={key} data-testid={`planner-content-total-${key}`} className="border border-gray-200 rounded-lg p-3 text-center">
                <Icon size={16} className="mx-auto text-gray-400 mb-1" />
                <div className="text-lg font-bold text-black">{count}</div>
                <div className="text-[10px] text-gray-500 leading-tight">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Date Selection */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Periodo de estudio</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Fecha inicio</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors"
              data-testid="start-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Fecha final</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors"
              data-testid="end-date-input"
            />
          </div>
        </div>
      </div>

      {/* Preview Stats */}
      {preview && (
        <div className="grid grid-cols-3 gap-2 bg-gray-50 rounded-xl p-3" data-testid="preview-stats">
          <div className="text-center">
            <div className="text-lg font-bold text-black">{preview.totalDays}</div>
            <div className="text-[10px] text-gray-500">Días totales</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-black">{preview.totalWeeks}</div>
            <div className="text-[10px] text-gray-500">Semanas</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-black">{preview.effectiveDays}</div>
            <div className="text-[10px] text-gray-500">Días efectivos</div>
          </div>
        </div>
      )}

      {/* Intensity */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Nivel de intensidad</h2>
        <div className="grid grid-cols-3 gap-2">
          {intensityOptions.map(opt => (
            <button
              key={opt.value}
              onClick={() => setIntensity(opt.value)}
              className={`border rounded-xl p-3 text-center transition-all ${
                intensity === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`intensity-${opt.value}`}
            >
              <div className={`text-xs font-bold mb-1 ${intensity === opt.value ? 'text-gray-300' : 'text-gray-400'}`}>{opt.icon}</div>
              <div className="text-sm font-semibold">{opt.label}</div>
              <div className={`text-[10px] mt-0.5 ${intensity === opt.value ? 'text-gray-300' : 'text-gray-500'}`}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Rest Day */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Día libre semanal</h2>
        <div className="grid grid-cols-2 gap-2">
          {[{ v: true, label: 'Sí', desc: '1 día libre/semana' }, { v: false, label: 'No', desc: 'Todos los días' }].map(opt => (
            <button
              key={String(opt.v)}
              onClick={() => setRestDay(opt.v)}
              className={`border rounded-xl p-3 text-center transition-all ${
                restDay === opt.v ? 'border-black bg-black text-white' : 'border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`rest-day-${opt.v}`}
            >
              <div className="text-sm font-semibold">{opt.label}</div>
              <div className={`text-[10px] mt-0.5 ${restDay === opt.v ? 'text-gray-300' : 'text-gray-500'}`}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={!startDate || !endDate || generating}
        className="w-full bg-black text-white rounded-xl py-3.5 font-semibold text-sm disabled:opacity-40 hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2"
        data-testid="generate-plan-btn"
      >
        {generating ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Generando plan...</span>
          </>
        ) : (
          <>
            <CalendarDays size={16} />
            <span>Generar mi plan de estudio</span>
          </>
        )}
      </button>
    </div>
  );
};

// ── Day Card ────────────────────────────────────────────────────────────────
const DayCard = ({ day, onToggle, isToday, isPast, prevDayArea, prevDaySubtema }) => {
  if (day.is_rest) {
    return (
      <div className="border border-dashed border-gray-200 rounded-xl p-4 opacity-60" data-testid={`day-${day.day_number}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-400">Día {day.day_number}</span>
          <span className="text-xs text-gray-400">{formatDate(day.date)}</span>
        </div>
        <p className="text-sm text-gray-400 italic text-center py-2">Día de descanso</p>
      </div>
    );
  }

  const completedCount = day.activities.filter(a => a.completed).length;
  const totalCount = day.activities.length;
  const progress = totalCount > 0 ? (completedCount / totalCount * 100) : 0;

  // Detect area changes within the day for separators
  const firstArea = day.activities.length > 0 ? day.activities[0].area : null;
  const showAreaHeader = firstArea && firstArea !== prevDayArea;

  // Group activities and detect area changes within day
  let currentArea = prevDayArea;
  let currentSubtema = prevDaySubtema || null;  // Track subtema for separator

  return (
    <>
      {showAreaHeader && firstArea !== 'Simulacro' && (
        <div className="my-5" data-testid={`area-separator-${day.day_number}`}>
          <div className="bg-black text-white rounded-xl py-3 px-4 text-center">
            <div className="text-lg font-bold tracking-wide uppercase">{firstArea}</div>
          </div>
        </div>
      )}
      <div
        className={`border rounded-xl p-4 transition-all ${
          isToday ? 'border-black ring-1 ring-black' : isPast ? 'border-gray-100 bg-gray-50/50' : 'border-gray-200'
        }`}
        data-testid={`day-${day.day_number}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-bold text-black">Día {day.day_number}</span>
            {isToday && <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">Hoy</span>}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-400">{formatDate(day.date)}</span>
            <span className="text-xs text-gray-400">{day.total_minutes}min</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-gray-100 rounded-full mb-3">
          <div className="h-1.5 bg-black rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>

        {/* Activities */}
        <div className="space-y-2">
          {day.activities.map((act, idx) => {
            const config = TYPE_CONFIG[act.type] || TYPE_CONFIG.cuestionario;
            const Icon = config.icon;
            const link = getActivityLink(act);

            // Detect area change within day
            const showInlineAreaSeparator = act.area !== currentArea && act.area !== 'Simulacro';
            if (act.area !== currentArea) {
              currentArea = act.area;
              currentSubtema = null; // Reset subtema when area changes
            }

            // Detect subtema change within day (specialty separator)
            const showSubtemaSeparator = act.subtema && act.subtema !== currentSubtema && act.area !== 'Simulacro';
            if (act.subtema !== currentSubtema) currentSubtema = act.subtema;

            return (
              <React.Fragment key={idx}>
                {showInlineAreaSeparator && idx > 0 && (
                  <div className="bg-black text-white rounded-lg py-2.5 px-3 text-center my-1">
                    <div className="text-base font-bold tracking-wide uppercase">{act.area}</div>
                  </div>
                )}
                {showSubtemaSeparator && act.subtema && (
                  <div className="bg-gray-800 text-white rounded-lg py-1.5 px-3 text-center my-1" data-testid={`subtema-separator-${day.day_number}-${idx}`}>
                    <div className="text-sm font-semibold tracking-wide">{act.subtema}</div>
                  </div>
                )}
                <div
                  className={`flex items-center space-x-2 rounded-lg px-3 py-2 border transition-all ${
                    act.completed
                      ? 'bg-gray-50 border-gray-200 opacity-60'
                      : `${config.color} border`
                  }`}
                  data-testid={`activity-${day.day_number}-${idx}`}
                >
                  {/* Checkbox only */}
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggle(day.day_number, idx, !act.completed); }}
                    disabled={act.unavailable}
                    className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform ${
                      act.completed ? 'bg-black border-black' : 'border-gray-400 hover:border-black'
                    }`}
                    data-testid={`checkbox-${day.day_number}-${idx}`}
                  >
                    {act.completed && <Check size={12} className="text-white" />}
                  </button>
                  <Icon size={14} className="flex-shrink-0" />
                  {/* Link text */}
                  {link ? (
                    <Link
                      to={link}
                      className={`text-sm leading-tight flex-1 min-w-0 [overflow-wrap:anywhere] hover:underline ${
                        act.completed ? 'line-through text-gray-400' : ''
                      }`}
                      data-testid={`link-${day.day_number}-${idx}`}
                    >
                      {cleanPlannerTitle(act.title)}
                    </Link>
                  ) : (
                    <span data-testid={`planner-activity-title-${day.day_number}-${idx}`} className={`text-sm leading-tight flex-1 min-w-0 [overflow-wrap:anywhere] ${act.completed ? 'line-through text-gray-400' : ''}`}>
                      {cleanPlannerTitle(act.title)}
                    </span>
                  )}
                  {act.unavailable && <span data-testid={`planner-unavailable-${day.day_number}-${idx}`} className="text-xs text-gray-500">No disponible</span>}
                  {act.vuelta > 1 && (
                    <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 rounded flex-shrink-0">v{act.vuelta}</span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="text-xs text-gray-400 mt-2 text-right">{completedCount}/{totalCount} completadas</div>
      </div>
    </>
  );
};

// ── Progress View ───────────────────────────────────────────────────────────
const ProgressView = ({ progress }) => {
  if (!progress) return null;

  const { global: g, by_area, by_type } = progress;

  return (
    <div className="space-y-5 pb-20" data-testid="progress-view">
      {/* Global Progress */}
      <div className="text-center">
        <div className="text-4xl font-bold text-black">{g.percent}%</div>
        <div className="text-xs text-gray-500 mt-1">{g.completed} de {g.total} actividades completadas</div>
        <div className="w-full h-2 bg-gray-100 rounded-full mt-3">
          <div className="h-2 bg-black rounded-full transition-all" style={{ width: `${g.percent}%` }} />
        </div>
      </div>

      {/* By Type */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Por tipo de contenido</h3>
        <div className="space-y-2">
          {Object.entries(by_type).map(([type, data]) => {
            const config = TYPE_CONFIG[type];
            if (!config) return null;
            const pct = data.total > 0 ? Math.round(data.completed / data.total * 100) : 0;
            return (
              <div key={type} className="flex items-center space-x-3">
                <config.icon size={14} className="text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex justify-between text-[11px] mb-0.5">
                    <span className="font-medium text-gray-700">{config.label}</span>
                    <span className="text-gray-400">{data.completed}/{data.total}</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full">
                    <div className="h-1.5 bg-black rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <span className="text-xs font-bold text-gray-700 w-8 text-right">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* By Area */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Por área</h3>
        <div className="space-y-2">
          {Object.entries(by_area).sort((a, b) => a[0].localeCompare(b[0])).map(([area, data]) => {
            const pct = data.total > 0 ? Math.round(data.completed / data.total * 100) : 0;
            return (
              <div key={area}>
                <div className="flex justify-between text-[11px] mb-0.5">
                  <span className="font-medium text-gray-700">{area}</span>
                  <span className="text-gray-400">{data.completed}/{data.total} ({pct}%)</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full">
                  <div className="h-1.5 bg-black rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Area Summary View ───────────────────────────────────────────────────────
const AREA_COLORS = {
  'Cirugía': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', bar: 'bg-rose-500', badge: 'bg-rose-100 text-rose-700' },
  'Ginecología y Obstetricia': { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', bar: 'bg-violet-500', badge: 'bg-violet-100 text-violet-700' },
  'Medicina Interna': { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', bar: 'bg-sky-500', badge: 'bg-sky-100 text-sky-700' },
  'Pediatría': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  'Otros': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700' },
};
const DEFAULT_AREA_COLOR = { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', bar: 'bg-gray-500', badge: 'bg-gray-100 text-gray-700' };

const AreaSummaryView = ({ plan }) => {
  const summary = useMemo(() => {
    if (!plan?.daily_plan) return [];
    const areaMap = {};
    const AREA_ORDER = { 'Cirugía': 0, 'Ginecología y Obstetricia': 1, 'Medicina Interna': 2, 'Pediatría': 3, 'Otros': 4 };

    plan.daily_plan.forEach(day => {
      if (day.is_rest) return;
      day.activities.forEach(act => {
        const area = act.area || 'Otros';
        if (area === 'Simulacro') return;
        if (!areaMap[area]) {
          areaMap[area] = {
            area,
            firstDay: day.day_number,
            lastDay: day.day_number,
            firstDate: day.date,
            lastDate: day.date,
            totalActivities: 0,
            totalMinutes: 0,
            days: new Set(),
            byType: {},
            vueltas: new Set(),
          };
        }
        const a = areaMap[area];
        if (day.day_number < a.firstDay) { a.firstDay = day.day_number; a.firstDate = day.date; }
        if (day.day_number > a.lastDay) { a.lastDay = day.day_number; a.lastDate = day.date; }
        a.days.add(day.day_number);
        a.totalActivities += 1;
        a.totalMinutes += act.weight || 0;
        a.byType[act.type] = (a.byType[act.type] || 0) + 1;
        if (act.vuelta) a.vueltas.add(act.vuelta);
      });
    });

    return Object.values(areaMap)
      .sort((a, b) => (AREA_ORDER[a.area] ?? 99) - (AREA_ORDER[b.area] ?? 99))
      .map(a => ({ ...a, dayCount: a.days.size, vueltaCount: a.vueltas.size }));
  }, [plan]);

  const totalActivities = summary.reduce((s, a) => s + a.totalActivities, 0);
  const totalDays = new Set(summary.flatMap(a => [...a.days])).size;

  // Timeline bar proportions
  const maxDay = plan?.daily_plan?.length || 1;

  return (
    <div className="space-y-4 pb-24" data-testid="area-summary-view">
      {/* Overview stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-black">{summary.length}</div>
          <div className="text-[10px] text-gray-500">Áreas</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-black">{totalDays}</div>
          <div className="text-[10px] text-gray-500">Días de estudio</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-black">{totalActivities}</div>
          <div className="text-[10px] text-gray-500">Actividades</div>
        </div>
      </div>

      {/* Timeline bar */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Línea de tiempo</h3>
        <div className="flex rounded-lg overflow-hidden h-6" data-testid="area-timeline">
          {summary.map(a => {
            const colors = AREA_COLORS[a.area] || DEFAULT_AREA_COLOR;
            const widthPct = Math.max((a.dayCount / totalDays) * 100, 2);
            return (
              <div
                key={a.area}
                className={`${colors.bar} flex items-center justify-center`}
                style={{ width: `${widthPct}%` }}
                title={`${a.area}: Días ${a.firstDay}-${a.lastDay}`}
              >
                <span className="text-white text-[9px] font-bold truncate px-1">
                  {a.area.length > 12 ? a.area.substring(0, 10) + '...' : a.area}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-gray-400">Día 1</span>
          <span className="text-[10px] text-gray-400">Día {maxDay}</span>
        </div>
      </div>

      {/* Area Cards */}
      <div className="space-y-3">
        {summary.map(a => {
          const colors = AREA_COLORS[a.area] || DEFAULT_AREA_COLOR;
          const pct = totalActivities > 0 ? Math.round((a.totalActivities / totalActivities) * 100) : 0;
          const hours = (a.totalMinutes / 60).toFixed(1);

          return (
            <div
              key={a.area}
              className={`border ${colors.border} ${colors.bg} rounded-xl p-4`}
              data-testid={`area-card-${a.area.replace(/\s+/g, '-')}`}
            >
              {/* Area header */}
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-base font-bold ${colors.text}`}>{a.area}</h3>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>{pct}%</span>
              </div>

              {/* Day range */}
              <div className="flex items-center space-x-2 mb-3">
                <CalendarDays size={13} className="text-gray-400" />
                <span className="text-sm text-gray-700 font-medium">
                  Días {a.firstDay} — {a.lastDay}
                </span>
                <span className="text-xs text-gray-400">
                  ({formatDate(a.firstDate)} — {formatDate(a.lastDate)})
                </span>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center bg-white/60 rounded-lg py-1.5">
                  <div className="text-sm font-bold text-black">{a.dayCount}</div>
                  <div className="text-[9px] text-gray-500">Días</div>
                </div>
                <div className="text-center bg-white/60 rounded-lg py-1.5">
                  <div className="text-sm font-bold text-black">{a.totalActivities}</div>
                  <div className="text-[9px] text-gray-500">Actividades</div>
                </div>
                <div className="text-center bg-white/60 rounded-lg py-1.5">
                  <div className="text-sm font-bold text-black">{hours}h</div>
                  <div className="text-[9px] text-gray-500">Tiempo est.</div>
                </div>
              </div>

              {/* Type breakdown */}
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(a.byType).map(([type, count]) => {
                  const config = TYPE_CONFIG[type];
                  if (!config) return null;
                  return (
                    <span key={type} className="inline-flex items-center space-x-1 text-[10px] bg-white/80 rounded-md px-2 py-1 border border-white">
                      <config.icon size={10} className="text-gray-500" />
                      <span className="text-gray-600">{count} {config.label}</span>
                    </span>
                  );
                })}
                {a.vueltaCount > 1 && (
                  <span className="inline-flex items-center text-[10px] bg-white/80 rounded-md px-2 py-1 border border-white text-gray-600">
                    {a.vueltaCount} vueltas
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function getWeekLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

// ── Doctor Coach (gamified rank figure + speech bubble) ──────────────────────
const COACH_MESSAGES = [
  '¡Vamos muy bien! Cada tema que estudias te acerca a tu ENARM.',
  '¡Excelente ritmo! La constancia es lo que hace al gran médico.',
  '¡Sigue así! Hoy es un gran día para sumar conocimiento.',
  '¡Tú puedes! Un paso a la vez, un tema a la vez.',
  '¡Gran trabajo! Tu futuro paciente te lo agradecerá.',
];

// Clean presentation titles for the coach (shared strip of topic+number prefixes)
const cleanPresentationTitle = (title) => cleanPlannerTitle(title);

const DoctorCoach = ({ user, focusDay, prevArea, prevSubtema }) => {
  const [rank, setRank] = useState(null);

  useEffect(() => {
    apiClient.get('/points/me')
      .then(r => setRank(r.data.current_rank))
      .catch(() => {});
  }, []);

  const gender = user?.gender || 'male';
  const spriteUrl = rank ? `/sprites/${rank.key}_${gender}.png?v=3` : null;

  // Presentation topics for the focused day (cleaned titles)
  const presentaciones = (focusDay && !focusDay.is_rest)
    ? focusDay.activities.filter(a => a.type === 'presentacion').map(a => cleanPresentationTitle(a.title))
    : [];

  // Current module (área) and subtema for the day (first non-simulacro activity)
  const firstAct = (focusDay && !focusDay.is_rest)
    ? focusDay.activities.find(a => a.area && a.area !== 'Simulacro')
    : null;
  const currentArea = firstAct ? firstAct.area : null;
  const currentSubtema = firstAct ? firstAct.subtema : null;

  // Module message: continuing vs transitioning
  let moduleMsg = '';
  if (currentArea) {
    if (prevArea && prevArea !== currentArea) {
      moduleMsg = `Hoy terminamos ${prevArea} y entramos al módulo de ${currentArea}.`;
    } else {
      moduleMsg = `Seguimos en el módulo de ${currentArea}.`;
    }
  }

  // Subtema message (only when subtema differs from the area name)
  let subtemaMsg = '';
  if (currentSubtema && currentSubtema !== currentArea) {
    if (prevSubtema && prevSubtema !== currentSubtema) {
      subtemaMsg = `Cambiamos al tema de ${currentSubtema}.`;
    } else {
      subtemaMsg = `Tema de hoy: ${currentSubtema}.`;
    }
  }

  // Stable-ish message per day
  const msgIdx = focusDay ? (focusDay.day_number % COACH_MESSAGES.length) : 0;
  const positiveMsg = COACH_MESSAGES[msgIdx];

  return (
    <div className="flex items-start gap-3 mb-4" data-testid="doctor-coach">
      {/* Rank figure */}
      <div className="flex-shrink-0 w-20 sm:w-24">
        {spriteUrl ? (
          <img src={spriteUrl} alt={rank?.name || 'Doctor'} className="w-full h-auto object-contain" data-testid="coach-sprite"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="w-full aspect-[3/4] bg-gray-100 rounded-lg animate-pulse" />
        )}
        {rank && <div className="text-[10px] text-gray-500 text-center font-medium mt-0.5 leading-tight">{rank.name}</div>}
      </div>

      {/* Speech bubble */}
      <div className="relative flex-1 bg-black text-white rounded-2xl rounded-tl-none p-3.5 shadow-sm" data-testid="coach-bubble">
        <div className="absolute -left-2 top-3 w-0 h-0 border-t-8 border-t-transparent border-r-8 border-r-black border-b-8 border-b-transparent" />
        <p className="text-sm font-semibold leading-snug">{positiveMsg}</p>
        {moduleMsg && (
          <p className="text-sm font-bold text-white mt-1.5" data-testid="coach-module-msg">{moduleMsg}</p>
        )}
        {subtemaMsg && (
          <p className="text-sm font-bold text-amber-300 mt-0.5" data-testid="coach-subtema-msg">{subtemaMsg}</p>
        )}
        {presentaciones.length > 0 ? (
          <div className="mt-2">
            <p className="text-[11px] text-gray-300 mb-1">Hoy te toca estudiar estos temas de Presentación:</p>
            <ul className="space-y-1">
              {presentaciones.map((title, i) => (
                <li key={i} className="text-sm font-bold text-blue-300 leading-tight flex items-start gap-1.5" data-testid={`coach-presentation-${i}`}>
                  <FileText size={13} className="flex-shrink-0 mt-0.5" />
                  <span>{title}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-[11px] text-gray-300 mt-1.5">
            {focusDay?.is_rest ? 'Hoy es día de descanso. ¡Recarga energías!' : 'Hoy repasarás cuestionarios y casos. ¡A darle!'}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
const StudyPlanner = ({ user }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar'); // 'calendar' | 'progress'
  const [viewMode, setViewMode] = useState('day'); // 'week' | 'day' — default to daily plan
  const [currentWeek, setCurrentWeek] = useState(0);
  const [currentDayIdx, setCurrentDayIdx] = useState(0);
  const [progress, setProgress] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load existing plan
  useEffect(() => {
    loadPlan();
  }, [user.id]);

  const loadPlan = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/planner/my-plan/${user.id}`);
      if (res.data.plan) {
        setPlan(res.data.plan);
        // Find current week based on today's date
        const today = new Date().toISOString().split('T')[0];
        const dailyPlan = res.data.plan.daily_plan || [];
        const todayIdx = dailyPlan.findIndex(d => d.date === today);
        if (todayIdx >= 0) {
          setCurrentWeek(Math.floor(todayIdx / 7));
          setCurrentDayIdx(todayIdx);
        }
      }
    } catch (err) {
      console.error('Error loading plan:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load progress when on progress tab
  useEffect(() => {
    if (activeTab === 'progress' && plan) {
      apiClient.get(`/planner/progress/${user.id}`)
        .then(r => setProgress(r.data.progress))
        .catch(() => toast.error('Error cargando progreso'));
    }
  }, [activeTab, plan, user.id]);

  const handleToggleActivity = async (dayNumber, actIdx, completed) => {
    try {
      await apiClient.put('/planner/complete-activity', {
        user_id: user.id, day_number: dayNumber, activity_index: actIdx, completed,
      });
      // Update local state
      setPlan(prev => {
        const newPlan = { ...prev, daily_plan: prev.daily_plan.map(d => {
          if (d.day_number === dayNumber) {
            const newActs = [...d.activities];
            newActs[actIdx] = { ...newActs[actIdx], completed };
            return { ...d, activities: newActs };
          }
          return d;
        })};
        return newPlan;
      });
    } catch {
      toast.error('Error actualizando actividad');
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await apiClient.post('/planner/recalculate', { user_id: user.id });
      setPlan(res.data.plan);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error recalculando');
    } finally {
      setRecalculating(false);
    }
  };

  const handleDeletePlan = async () => {
    try {
      await apiClient.delete(`/planner/delete/${user.id}`);
      setPlan(null);
      setShowDeleteConfirm(false);
      toast.success('Plan eliminado');
    } catch {
      toast.error('Error eliminando plan');
    }
  };

  // ── Weekly chunks ─────────────────────────────────────────────────────────
  const weeks = useMemo(() => {
    if (!plan?.daily_plan) return [];
    const w = [];
    for (let i = 0; i < plan.daily_plan.length; i += 7) {
      w.push(plan.daily_plan.slice(i, i + 7));
    }
    return w;
  }, [plan]);

  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-3 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto flex justify-between items-center">
          <Link to="/" data-testid="back-btn" className="flex items-center space-x-2 text-black text-sm font-medium hover:text-gray-600 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Menú</span>
          </Link>
          <h1 className="text-sm font-bold text-black tracking-tight">Planificador de estudio</h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="pt-14 px-4 max-w-3xl mx-auto">
        {!plan ? (
          <SetupView user={user} onPlanGenerated={(p) => { setPlan(p); setActiveTab('calendar'); }} />
        ) : (
          <>
            {/* Plan Summary Bar */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4" data-testid="plan-summary">
              <div className="grid grid-cols-5 gap-1 text-center">
                <div>
                  <div className="text-sm font-bold text-black">{plan.effective_days}</div>
                  <div className="text-[9px] text-gray-500">Días</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-black">{plan.estimated_vueltas}x</div>
                  <div className="text-[9px] text-gray-500">Vueltas</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-black">{plan.total_activities}</div>
                  <div className="text-[9px] text-gray-500">Actividades</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-black">{(plan.avg_daily_minutes / 60).toFixed(1)}h</div>
                  <div className="text-[9px] text-gray-500">Carga diaria</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-black capitalize">{plan.intensity}</div>
                  <div className="text-[9px] text-gray-500">Nivel</div>
                </div>
              </div>
              <div className="text-[10px] text-gray-400 text-center mt-1.5">
                {plan.start_date} al {plan.end_date}
              </div>
            </div>

            {/* Warning */}
            {plan.warning && (
              <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700" data-testid="plan-warning">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{plan.warning}</span>
              </div>
            )}

            {/* Tabs */}
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-0.5 mb-3">
              {[
                { id: 'calendar', label: 'Calendario', Icon: CalendarDays },
                { id: 'summary', label: 'Resumen', Icon: LayoutGrid },
                { id: 'progress', label: 'Progreso', Icon: BarChart3 },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-1.5 py-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === tab.id ? 'bg-white text-black shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.Icon size={13} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {activeTab === 'calendar' && (
              <>
                {/* Doctor coach: rank figure + speech bubble with today's presentation topics */}
                {(() => {
                  const focusDay = viewMode === 'day'
                    ? plan.daily_plan[currentDayIdx]
                    : (plan.daily_plan.find(d => d.date === today) || plan.daily_plan[currentDayIdx]);
                  // Previous study day's module (área) and subtema to detect transitions
                  let prevArea = null;
                  let prevSubtema = null;
                  if (focusDay) {
                    const before = plan.daily_plan.filter(d => d.day_number < focusDay.day_number && !d.is_rest && d.activities.length > 0);
                    const prevDay = before.length > 0 ? before[before.length - 1] : null;
                    if (prevDay) {
                      const acts = prevDay.activities.filter(a => a.area && a.area !== 'Simulacro');
                      if (acts.length > 0) {
                        prevArea = acts[acts.length - 1].area;
                        prevSubtema = acts[acts.length - 1].subtema;
                      }
                    }
                  }
                  return <DoctorCoach user={user} focusDay={focusDay} prevArea={prevArea} prevSubtema={prevSubtema} />;
                })()}
                {/* Color Legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3" data-testid="color-legend">
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <div key={key} className="flex items-center space-x-1.5">
                      <div className={`w-3.5 h-3.5 rounded ${cfg.color.split(' ')[0]} border ${cfg.color.split(' ')[2]}`} />
                      <span className="text-xs text-gray-600">{cfg.label}</span>
                    </div>
                  ))}
                </div>

                {/* View Mode & Week Nav */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex space-x-1 bg-gray-100 rounded-md p-0.5">
                    {['week', 'day'].map(m => (
                      <button
                        key={m}
                        onClick={() => setViewMode(m)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                          viewMode === m ? 'bg-white text-black shadow-sm' : 'text-gray-500'
                        }`}
                        data-testid={`view-${m}`}
                      >
                        {m === 'week' ? 'Semanal' : 'Diario'}
                      </button>
                    ))}
                  </div>

                  {viewMode === 'week' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentWeek(w => Math.max(0, w - 1))}
                        disabled={currentWeek === 0}
                        className="p-1 rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                        data-testid="prev-week"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <span className="text-xs font-medium text-gray-600">
                        Semana {currentWeek + 1}/{weeks.length}
                      </span>
                      <button
                        onClick={() => setCurrentWeek(w => Math.min(weeks.length - 1, w + 1))}
                        disabled={currentWeek >= weeks.length - 1}
                        className="p-1 rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                        data-testid="next-week"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  )}

                  {viewMode === 'day' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentDayIdx(i => Math.max(0, i - 1))}
                        disabled={currentDayIdx === 0}
                        className="p-1 rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                        data-testid="prev-day"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <span className="text-xs font-medium text-gray-600">
                        {plan.daily_plan[currentDayIdx] && formatDate(plan.daily_plan[currentDayIdx].date)}
                      </span>
                      <button
                        onClick={() => setCurrentDayIdx(i => Math.min((plan.daily_plan?.length || 1) - 1, i + 1))}
                        disabled={currentDayIdx >= (plan.daily_plan?.length || 1) - 1}
                        className="p-1 rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
                        data-testid="next-day"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Calendar Content */}
                <div className="space-y-3 pb-24">
                  {viewMode === 'week' && weeks[currentWeek]?.map((day, idx) => {
                    // Get last area/subtema from previous day for separator logic
                    const allDaysBefore = plan.daily_plan.filter(d => d.day_number < day.day_number && !d.is_rest);
                    const prevDay = allDaysBefore.length > 0 ? allDaysBefore[allDaysBefore.length - 1] : null;
                    const prevArea = prevDay?.activities?.length > 0 ? prevDay.activities[prevDay.activities.length - 1].area : null;
                    const prevSubtema = prevDay?.activities?.length > 0 ? prevDay.activities[prevDay.activities.length - 1].subtema : null;
                    return (
                      <DayCard
                        key={day.day_number}
                        day={day}
                        onToggle={handleToggleActivity}
                        isToday={day.date === today}
                        isPast={day.date < today}
                        prevDayArea={prevArea}
                        prevDaySubtema={prevSubtema}
                      />
                    );
                  })}
                  {viewMode === 'day' && plan.daily_plan[currentDayIdx] && (() => {
                    const allDaysBefore = plan.daily_plan.filter(d => d.day_number < plan.daily_plan[currentDayIdx].day_number && !d.is_rest);
                    const prevDay = allDaysBefore.length > 0 ? allDaysBefore[allDaysBefore.length - 1] : null;
                    const prevArea = prevDay?.activities?.length > 0 ? prevDay.activities[prevDay.activities.length - 1].area : null;
                    const prevSubtema = prevDay?.activities?.length > 0 ? prevDay.activities[prevDay.activities.length - 1].subtema : null;
                    return (
                      <DayCard
                        day={plan.daily_plan[currentDayIdx]}
                        onToggle={handleToggleActivity}
                        isToday={plan.daily_plan[currentDayIdx].date === today}
                        isPast={plan.daily_plan[currentDayIdx].date < today}
                        prevDayArea={prevArea}
                        prevDaySubtema={prevSubtema}
                      />
                    );
                  })()}
                </div>

                {/* Actions bar */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-3 z-40">
                  <div className="max-w-3xl mx-auto">
                    {showDeleteConfirm ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-600 flex-1">¿Eliminar plan actual?</span>
                        <button
                          onClick={handleDeletePlan}
                          className="flex items-center justify-center space-x-1 py-2.5 px-4 bg-red-600 text-white rounded-xl text-xs font-medium hover:bg-red-700 transition-colors"
                          data-testid="confirm-delete-btn"
                        >
                          <Trash2 size={13} />
                          <span>Sí, eliminar</span>
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="py-2.5 px-4 border border-gray-200 rounded-xl text-xs font-medium hover:bg-gray-50 transition-colors"
                          data-testid="cancel-delete-btn"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleRecalculate}
                          disabled={recalculating}
                          className="flex-1 flex items-center justify-center space-x-1.5 py-2.5 border border-gray-200 rounded-xl text-xs font-medium hover:bg-gray-50 transition-colors disabled:opacity-40"
                          data-testid="recalculate-btn"
                        >
                          <RefreshCw size={13} className={recalculating ? 'animate-spin' : ''} />
                          <span>Recalcular</span>
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="flex items-center justify-center space-x-1.5 py-2.5 px-4 border border-red-200 text-red-600 rounded-xl text-xs font-medium hover:bg-red-50 transition-colors"
                          data-testid="delete-plan-btn"
                        >
                          <Trash2 size={13} />
                          <span>Nuevo plan</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'summary' && (
              <AreaSummaryView plan={plan} />
            )}

            {activeTab === 'progress' && (
              <ProgressView progress={progress} />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default StudyPlanner;
